import React, { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';

import last from 'lodash/last';

import { isCancel } from '@lumapps/base-api';
import { useInfiniteQuery, type UseInfiniteQueryOptions } from '@lumapps/base-api/react-query';
import { getServerErrorData } from '@lumapps/base-api/utils/getServerError';
import { CACHE_TYPE, cache } from '@lumapps/cache';
import { get as getConstants } from '@lumapps/constants';
import { useContent } from '@lumapps/contents/hooks/useContent';
import { notifySubscribers, useAdditionalFilters } from '@lumapps/customizations/api';
import { Events } from '@lumapps/customizations/types';
import { useDispatch, useSelector } from '@lumapps/redux/react';
import { useDimensions } from '@lumapps/responsive';
import { AppId } from '@lumapps/router';
import { inputLanguageSelector } from '@lumapps/translations/ducks/selectors';
import { requestOnIdleCallback } from '@lumapps/utils/function/requestIdleCallback';
import { useIsInView } from '@lumapps/utils/hooks/useIsInView';
import { useMergedRefObject } from '@lumapps/utils/hooks/useMergedRefObject';
import { useUpdatedRef } from '@lumapps/utils/hooks/useUpdatedRef';
import type { WidgetReference } from '@lumapps/widget-layout/types';

import { widgetApi, paths, queryKeys, type FetchBlockParams } from '../../api';
import {
    WIDGET_SKELETON_CACHE_KEY,
    WIDGET_SIZE_BREAKPOINTS,
    WIDGET_SKELETON_MAX_PAGES_SAVED,
    WIDGET_IN_VIEW_CONFIGURATION,
    RETROCOMPAT_MONOLANG_WIDGETS,
} from '../../constants';
import { fetchWidgetBlocksById } from '../../ducks/thunks/fetchWidgetBlocksById';
import { fetchWidgetBlocksByProperties } from '../../ducks/thunks/fetchWidgetBlocksByProperties';
import { useWidgetURLFilters } from '../../hooks/useWidgetURLFilters';
import {
    isArrayContainerBlock,
    isSingleContainerBlock,
    type ArrayContainerBlock,
    type BaseSingleContainerBlock,
    type Blocks,
    type Widget,
    type WidgetLoadingState,
} from '../../types';
import type { Widget as WidgetType } from '../../types';
import { getEffectiveWidgetType } from '../../utils/getEffectiveWidgetType';
import { getWhitelistedPropsForCustomisationsApi } from '../../utils/getWhitelistedPropsForCustomisationsApi';
import { getWidgetProperties } from '../../utils/getWidgetProperties';
import type { ConnectedWidgetProps } from './ConnectedWidget';
import { useOwnerResourceInfo } from './useOwnerResourceInfo';
import { useWidgetState } from './useWidgetState';
import type { WidgetProps } from './Widget';

const MAX_FETCH_RETRY = 2;

/**
 * React Query options that can be customized for widget fetching.
 * Excludes options that are computed internally (queryKey, queryFn, etc.)
 */
export type WidgetQueryProps = Pick<UseInfiniteQueryOptions<WidgetType>, 'enabled' | 'staleTime' | 'cacheTime'>;

/**
 * useWidget input options
 */
type Options<WidgetSpecificProperties = object> = Pick<
    WidgetReference<WidgetSpecificProperties>,
    'widgetId' | 'widgetType' | 'legacyWidget' | 'isMainWidget'
> &
    Pick<ConnectedWidgetProps<WidgetSpecificProperties>, 'excludedQueryKeyWidgetProperties'> & {
        /** Optional React Query configuration to customize caching and fetching behavior */
        queryProps?: WidgetQueryProps;
        /** When true, returns the fixed owner resource info from Redux instead of live form values */
        shouldReturnFixedOwnerInfo?: boolean;
    };

/**
 * useWidget output
 */
type Output = Pick<WidgetProps, 'widget' | 'widgetWidth' | 'fetchMore' | 'refetch'> & {
    widgetRef: React.RefObject<HTMLElement>;
};
/**
 * Full widget connected logic
 * - Fetching block (if needed) from id or properties
 * - Fetch more
 * - Compute states
 * - Viewport detection
 * etc.
 */
export const useWidget = (widgetReference: Options): Output => {
    const constants = useMemo(() => getConstants(), []);
    const isDesignerApp = constants.applicationId === AppId.designer;

    const {
        isMainWidget,
        widgetType = '',
        widgetId = '',
        legacyWidget,
        excludedQueryKeyWidgetProperties,
        queryProps,
        shouldReturnFixedOwnerInfo,
    } = widgetReference;
    const widget = useWidgetState(widgetId);
    const { isPreloaded, filters, state, blockResolutionInfo } = widget;
    const effectiveWidgetType = getEffectiveWidgetType(widget) || widgetType;

    const additionalFilters = useAdditionalFilters(effectiveWidgetType);

    const dispatch = useDispatch();
    const fetch = React.useCallback(
        ({ cursor, ...params }: Partial<FetchBlockParams>): Promise<WidgetType> => {
            if (legacyWidget) {
                return dispatch(
                    fetchWidgetBlocksByProperties({
                        legacyWidget,
                        params: {
                            ...widget?.blockResolutionInfo,
                            ...params,
                            filters,
                            combinedFilters: additionalFilters,
                            cursor,
                        },
                        isMainWidget: Boolean(isMainWidget),
                        skipErrorHandling: true,
                    }),
                ) as any;
            }
            return dispatch(
                fetchWidgetBlocksById({
                    widgetId,
                    widgetType,
                    params: {
                        ...widget?.blockResolutionInfo,
                        ...params,
                        filters,
                        combinedFilters: additionalFilters,
                        cursor,
                    },
                    isMainWidget: Boolean(isMainWidget),
                    skipErrorHandling: true,
                }),
            ) as any;
        },
        [
            legacyWidget,
            dispatch,
            widgetId,
            widgetType,
            widget?.blockResolutionInfo,
            filters,
            additionalFilters,
            isMainWidget,
        ],
    );

    const { pageId } = useContent();

    const inputLanguage = useSelector(inputLanguageSelector);
    const ownerResourceInfo = useOwnerResourceInfo(shouldReturnFixedOwnerInfo ?? false);
    const { ownerResourceId = '', ownerResourceType = '' } = ownerResourceInfo ?? {};

    useWidgetURLFilters({ widgetId, widgetType, isMainWidget });

    const { ref: inViewRef, inView: isWidgetInView } = useIsInView(WIDGET_IN_VIEW_CONFIGURATION);
    const { ref: dimensionRef, currentBreakpoint: widgetWidth } = useDimensions({
        breakpoints: WIDGET_SIZE_BREAKPOINTS,
    });
    const widgetRef = useMergedRefObject<HTMLElement>(inViewRef, dimensionRef);
    const isWidgetLoaded = state === 'loaded';

    useEffect(() => {
        /**
         * If there are any subscribers for a widget render, we execute this callback
         * when the state is loaded, and we only provide the props that we have whitelisted
         * to provide to the customisations api.
         */
        if (state === 'loaded') {
            notifySubscribers(getWhitelistedPropsForCustomisationsApi(widget), Events.WIDGET_RENDERED);
        }
        // Ignoring the react hooks rule since we do not want to re-execute this function
        // when props change, only when the state does
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);

    // Store ownerResourceInfo in a ref to use it in the queryFn without adding it in the queryKey.
    const ownerResourceRef = useUpdatedRef(ownerResourceInfo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const getUpdatedOwnerResourceRef = useCallback(() => ownerResourceRef.current, []);

    // Filter widget properties for queryKey, if needed
    const properties = React.useMemo(
        () => getWidgetProperties(legacyWidget, excludedQueryKeyWidgetProperties),
        [excludedQueryKeyWidgetProperties, legacyWidget],
    );

    // Compute the enabled state for the query
    // In designer widgets are potentially refetched when they enter the viewport.
    // But in view mode, we don't want to refetch already loaded widgets.
    const isQueryEnabled = React.useMemo(() => {
        if (queryProps?.enabled !== undefined) {
            return queryProps.enabled;
        }
        const shouldFetchOnInView = isDesignerApp ? isWidgetInView : isWidgetInView || isWidgetLoaded;
        return shouldFetchOnInView && !isPreloaded && !!effectiveWidgetType;
    }, [queryProps?.enabled, isDesignerApp, isWidgetInView, isWidgetLoaded, isPreloaded, effectiveWidgetType]);

    const {
        data,
        fetchNextPage,
        isFetching,
        isError,
        isRefetching,
        isFetchingNextPage,
        isPlaceholderData,
        refetch,
        error,
    } = useInfiniteQuery({
        // Spread custom query props first, so they can be overridden by computed values
        ...queryProps,
        enabled: isQueryEnabled,
        // eslint-disable-next-line @tanstack/query/exhaustive-deps
        queryKey: queryKeys.getWidgetBlocks({
            ownerResourceId,
            ownerResourceType,
            widgetType: effectiveWidgetType,
            widgetId,
            language: inputLanguage,
            properties,
            filters: additionalFilters ? { ...filters, ...additionalFilters } : filters,
        }),
        queryFn: ({ pageParam }) => {
            const lastOwnerResourceInfo = getUpdatedOwnerResourceRef();

            // In designer, we need to disable language fallbacks by default even if the associated property (disableFallback) is false.
            // Some widgets are no longer multilang but the option still works so we don't want to override for them.
            const canUseLangFallback =
                isDesignerApp && !RETROCOMPAT_MONOLANG_WIDGETS.includes(effectiveWidgetType) ? false : undefined;
            return fetch({
                cursor: pageParam?.cursor,
                canUseLangFallback,
                ownerResourceInfo: lastOwnerResourceInfo,
            });
        },
        staleTime: isDesignerApp ? Infinity : queryProps?.staleTime ?? 0,
        retry: MAX_FETCH_RETRY,
        retryDelay: 0,
        initialData: () => {
            if (widgetType === 'client-computed') {
                return { pages: [widget], pageParams: [] };
            }
            return undefined;
        },
        // Initialize with the widget object from the template.
        placeholderData: () => ({ pages: [widget], pageParams: [] }),
        getNextPageParam: (lastPage) => ({ cursor: lastPage?.more && lastPage.cursor }),
    });

    // This is to consolidate pagination since the items could be nested in blocks.
    const queriedWidget = useMemo((): Widget => {
        const queryPages = data?.pages || [];

        if (widget?.isPreloaded) {
            // queryPages[0] may be undefined on the first render after the query becomes enabled
            // (before placeholderData is applied). Fall back to the preloaded widget from the store.
            return (queryPages[0] ?? widget) as Widget;
        }

        const previousWidgetRender = { ...last(queryPages), isMainWidget, blockResolutionInfo, widgetId } as Widget;

        // List block
        if (!!previousWidgetRender?.body && isArrayContainerBlock(previousWidgetRender.body)) {
            const consolidatedItems = queryPages.reduce<Blocks[]>(
                (items, page) => [...items, ...(page.body as ArrayContainerBlock).items],
                [],
            );
            return {
                ...previousWidgetRender,
                body: { ...previousWidgetRender.body, items: consolidatedItems },
            } as Widget;
        }

        // List nested in a SingleContainer (ex: BlockAugmentedContainer)
        if (
            !!previousWidgetRender?.body &&
            isSingleContainerBlock(previousWidgetRender.body) &&
            (isArrayContainerBlock(previousWidgetRender.body.container) ||
                previousWidgetRender.body.container.type === 'BlockNoResults')
        ) {
            const consolidatedItems = queryPages.reduce<Blocks[]>((items, page) => {
                const newItems = ((page.body as BaseSingleContainerBlock).container as ArrayContainerBlock).items;
                if (!newItems) {
                    return items;
                }

                return [...items, ...((page.body as BaseSingleContainerBlock).container as ArrayContainerBlock).items];
            }, []);
            return {
                ...previousWidgetRender,
                body: {
                    ...previousWidgetRender.body,
                    container: { ...previousWidgetRender.body.container, items: consolidatedItems },
                },
            } as Widget;
        }

        // Not paginated cases
        return previousWidgetRender;
    }, [blockResolutionInfo, data?.pages, isMainWidget, widget, widgetId]);

    const widgetStatus: WidgetLoadingState | undefined = useMemo(() => {
        const hasBody = !!queriedWidget?.body;
        const isClientComputed = queriedWidget.widgetType === 'client-computed';

        if (isPreloaded) {
            if (isClientComputed) {
                return queriedWidget?.isClientComputedEmpty ? 'empty' : 'loaded';
            }
            return hasBody ? 'loaded' : 'empty';
        }

        if (isClientComputed) {
            return queriedWidget?.isClientComputedEmpty ? 'empty' : 'loaded';
        }

        if (isError && error) {
            if (error.response?.status === 400) {
                if (getServerErrorData(error.response)?.code === 'WIDGET_UNSUPPORTED_SETTINGS') {
                    // Widget block request not migrated.
                    return 'notmigrated';
                }
                return undefined;
            }

            if (error.response?.status === 403) {
                // Widget block request restricted.
                return 'restricted';
            }

            if (isCancel(error)) {
                // Widget block request canceled.
                return 'cancelled';
            }

            return 'error';
        }

        // Widget not fetched yet
        if (isPlaceholderData && !hasBody) {
            if (isRefetching && filters) {
                return 'loadingcontent';
            }
            return 'loading';
        }

        if (isFetching) {
            if (isFetchingNextPage) {
                return 'loadingmore';
            }
            if (filters) {
                return 'loadingcontent';
            }

            // Will be set to "refreshing" only if the widget has already been fetched
            // and is not fetching a new page nor filtering.
            // It is useful to handle properties changes in the content editor.
            return 'refreshing';
        }

        const isListEmpty = widget?.body?.type === 'BlockNoResults' && !filters;
        const isAugmentedListEmpty =
            !filters &&
            widget?.body &&
            isSingleContainerBlock(widget?.body) &&
            widget?.body?.container.type === 'BlockNoResults';

        if (isAugmentedListEmpty || isListEmpty || !hasBody) {
            return 'empty';
        }

        return 'loaded';
    }, [
        queriedWidget,
        error,
        isPreloaded,
        isError,
        isPlaceholderData,
        isFetching,
        widget?.body,
        filters,
        isRefetching,
        isFetchingNextPage,
    ]);

    /**
     * In order to enhance the look and feel and the loading of the page, we save in localstorage the height of each
     * rendered widget, so we can then use in the next render to display a more faithful skeleton. This is currently in beta,
     * since I (Juan) am not sure that adding the height of all widgets on the platform is the best idea, localstorage does have a limit.
     * But I would like to test this around and see what it does.
     */
    useLayoutEffect(() => {
        if (widgetStatus === 'loaded' && widgetId) {
            requestOnIdleCallback(() => {
                let widgetSkeletons = cache.retrieve(WIDGET_SKELETON_CACHE_KEY, CACHE_TYPE.STORAGE) || {};

                /**
                 * If there are more than WIDGET_SKELETON_MAX_PAGES_SAVED, we need to clean up our local storage.
                 * We always want to have the home saved, so in this case we retrieve the skeletons for the home page
                 * (if there are any) and we then recreate the widgetSkeletons with those values.
                 */
                if (Object.keys(widgetSkeletons).length > WIDGET_SKELETON_MAX_PAGES_SAVED) {
                    widgetSkeletons = {
                        home: widgetSkeletons.home || {},
                    };
                }

                if (pageId) {
                    const skeletonsForPage = widgetSkeletons[pageId] || {};

                    cache.store(
                        WIDGET_SKELETON_CACHE_KEY,
                        {
                            ...widgetSkeletons,
                            [pageId]: {
                                ...skeletonsForPage,
                                [widgetId]: widgetRef.current?.clientHeight,
                            },
                        },
                        CACHE_TYPE.STORAGE,
                    );
                }
            });
        }
    }, [pageId, widgetId, widgetRef, widgetStatus]);

    useEffect(
        () =>
            /**
             * Cancels widget-related unfinished requests if this component is unmounted before the end of these requests.
             * For instance, when switching page, we don't want to block the navigation by waiting for useless requests.
             */
            () => {
                if (!widgetType || !widgetId) {
                    return;
                }
                widgetApi.cancel(paths.blockById(widgetType, widgetId), {});
            },
        [widgetType, widgetId],
    );

    return {
        widgetWidth,
        widgetRef,
        widget: { ...widget, ...queriedWidget, state: widgetStatus },
        refetch,
        fetchMore: fetchNextPage,
    };
};
